import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 生物科学 - 横屏「DNA 与基因」风格
 *
 * 核心亮点：
 * - SVG DNA 双螺旋旋转动画
 * - 碱基对标注（A-T / G-C）
 * - 基因信息卡片
 * - 关键数据面板
 *
 * 尺寸：1920x1080 横屏
 */

interface GeneInfo {
  name: string;
  description: string;
  icon: string;
}

interface BioStat {
  label: string;
  value: string;
  icon: string;
}

interface BiologyDNATemplateProps {
  topic: string;
  subtitle: string;
  genes: GeneInfo[];
  stats: BioStat[];
  basePairs: { left: string; right: string; color: string }[];
  keyFact: string;
  discoveries: string[];
}

const DEFAULT_PROPS: BiologyDNATemplateProps = {
  topic: 'DNA 双螺旋结构',
  subtitle: 'The Double Helix — Blueprint of Life',
  genes: [
    { name: '碱基配对原则', description: '腺嘌呤(A)与胸腺嘧啶(T)配对，鸟嘌呤(G)与胞嘧啶(C)配对', icon: '🧬' },
    { name: '半保留复制', description: 'DNA 复制时每条新链保留一条母链作为模板', icon: '🔄' },
    { name: '中心法则', description: 'DNA → RNA → 蛋白质，遗传信息的传递方向', icon: '📋' },
  ],
  stats: [
    { label: '碱基对数量', value: '32 亿', icon: '🔢' },
    { label: '螺旋直径', value: '2nm', icon: '📏' },
    { label: '每圈碱基对', value: '10 对', icon: '🔗' },
    { label: '全长(展开)', value: '~2m', icon: '📐' },
  ],
  basePairs: [
    { left: 'A', right: 'T', color: '#ef4444' },
    { left: 'G', right: 'C', color: '#3b82f6' },
    { left: 'T', right: 'A', color: '#22c55e' },
    { left: 'C', right: 'G', color: '#f59e0b' },
    { left: 'A', right: 'T', color: '#ef4444' },
    { left: 'G', right: 'C', color: '#3b82f6' },
    { left: 'T', right: 'A', color: '#22c55e' },
    { left: 'C', right: 'G', color: '#f59e0b' },
  ],
  keyFact: '如果将人体所有细胞的 DNA 展开首尾相连，总长度可以往返地球到太阳 600 多次',
  discoveries: [
    '1953 年 Watson & Crick 提出双螺旋模型',
    '2003 年人类基因组计划完成',
    'CRISPR-Cas9 基因编辑技术革命',
  ],
};

export const BiologyDNATemplate: React.FC<Partial<BiologyDNATemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const helixDraw = interpolate(frame, [20, 90], [0, 1], { extrapolateRight: 'clamp' });

  const geneStart = 100;
  const geneInterval = 50;
  const statStart = geneStart + p.genes.length * geneInterval + 20;
  const factStart = statStart + 80;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const discStart = factStart + 40;

  const helixCx = 360;
  const helixW = 160;
  const pairCount = p.basePairs.length;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 40%, #020617 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Subtle particles */}
      {Array.from({ length: 20 }).map((_, i) => {
        const x = (i * 237) % 1920;
        const y = ((i * 173 + frame * 0.3) % 1080);
        return (
          <div key={i} style={{
            position: 'absolute', left: x, top: y,
            width: 3, height: 3, borderRadius: '50%',
            background: '#818cf8', opacity: 0.15,
          }} />
        );
      })}

      {/* ====== LEFT COLUMN: Header + DNA Helix ====== */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: 780, height: 1080 }}>
        {/* Header */}
        <div style={{ position: 'absolute', top: 50, left: 60, right: 40, opacity: headerProgress }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ padding: '6px 18px', background: '#22c55e15', border: '1px solid #22c55e35', borderRadius: 10, fontSize: 22, color: '#22c55e', fontWeight: 700 }}>
              🧬 BIOLOGY
            </div>
          </div>
          <h1 style={{ fontSize: 44, fontWeight: 900, margin: 0 }}>{p.topic}</h1>
          <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginTop: 6 }}>{p.subtitle}</p>
        </div>

        {/* DNA Helix SVG */}
        <svg style={{ position: 'absolute', left: 0, top: 260 }} width="780" height="620" viewBox="0 0 720 620">
          {Array.from({ length: pairCount }).map((_, i) => {
            const yBase = 40 + i * 70;
            const phase = frame * 0.04 + i * 0.8;
            const xOffset = Math.sin(phase) * helixW * 0.5;
            const depth = Math.cos(phase);
            const pairOpacity = interpolate(helixDraw, [i / pairCount, Math.min((i + 1) / pairCount, 1)], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
            const bp = p.basePairs[i];
            if (!bp) return null;

            const lx = helixCx - xOffset;
            const rx = helixCx + xOffset;

            return (
              <g key={i} opacity={pairOpacity}>
                {i < pairCount - 1 && (
                  <>
                    <line x1={lx} y1={yBase} x2={helixCx - Math.sin(phase + 0.8) * helixW * 0.5} y2={yBase + 70}
                      stroke="#22c55e" strokeWidth="2.5" opacity="0.2" />
                    <line x1={rx} y1={yBase} x2={helixCx + Math.sin(phase + 0.8) * helixW * 0.5} y2={yBase + 70}
                      stroke="#818cf8" strokeWidth="2.5" opacity="0.2" />
                  </>
                )}
                <line x1={lx} y1={yBase} x2={rx} y2={yBase}
                  stroke={bp.color} strokeWidth="2" opacity={0.3 + Math.abs(depth) * 0.3}
                  strokeDasharray="4 3" />
                <circle cx={lx} cy={yBase} r={14} fill={bp.color} opacity={0.6 + depth * 0.2} />
                <text x={lx} y={yBase + 5} textAnchor="middle" fill="white" fontSize="13" fontWeight="800">{bp.left}</text>
                <circle cx={rx} cy={yBase} r={14} fill={bp.color} opacity={0.6 - depth * 0.2} />
                <text x={rx} y={yBase + 5} textAnchor="middle" fill="white" fontSize="13" fontWeight="800">{bp.right}</text>
              </g>
            );
          })}
        </svg>

        {/* Key fact */}
        <div style={{ position: 'absolute', bottom: 50, left: 50, right: 30, opacity: factOpacity }}>
          <div style={{ padding: '16px 22px', background: '#071a12', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 14 }}>
            <span style={{ fontSize: 20, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>🌟 {p.keyFact}</span>
          </div>
        </div>
      </div>

      {/* Vertical divider */}
      <div style={{
        position: 'absolute', left: 800, top: 60, bottom: 60,
        width: 1, background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0.06) 70%, transparent)',
      }} />

      {/* ====== RIGHT COLUMN: Cards + Stats + Discoveries ====== */}
      <div style={{ position: 'absolute', left: 840, top: 130, right: 60, bottom: 50, display: 'flex', flexDirection: 'column' }}>

        {/* Gene Info Cards */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, letterSpacing: '0.1em', fontWeight: 600 }}>核心知识点</div>
          {p.genes.map((gene, i) => {
            const start = geneStart + i * geneInterval;
            const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 14, stiffness: 120 } });
            return (
              <div
                key={i}
                style={{
                  padding: '14px 18px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.06)',
                  marginBottom: 10,
                  opacity: progress,
                  transform: `translateX(${(1 - progress) * 20}px)`,
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>{gene.icon} {gene.name}</div>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{gene.description}</div>
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 12, fontWeight: 600 }}>📊 关键数据</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {p.stats.map((stat, i) => {
              const progress = spring({ frame: frame - statStart - i * 15, fps, from: 0, to: 1, config: { damping: 12, stiffness: 140 } });
              return (
                <div key={i} style={{
                  padding: '14px 16px', background: 'rgba(255,255,255,0.03)',
                  borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
                  opacity: progress, display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 32 }}>{stat.icon}</span>
                  <div>
                    <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>{stat.label}</div>
                    <div style={{ fontSize: 26, fontWeight: 800 }}>{stat.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Discoveries */}
        <div>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 10, fontWeight: 600 }}>🏆 里程碑发现</div>
          {p.discoveries.map((disc, i) => {
            const opacity = interpolate(frame, [discStart + i * 25, discStart + i * 25 + 18], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, opacity }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#22c55e', marginTop: 11, flexShrink: 0 }} />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{disc}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: 'linear-gradient(90deg, #22c55e, #3b82f6)' }} />
    </AbsoluteFill>
  );
};
